import React, { useState, useEffect } from 'react';
import { useAbdm } from '../context/AbdmContext';
import { getAbhaSuggestions, handleCreateABHA } from '../../utils/api';

type AddressMode = 'existing' | 'suggestion' | 'custom';

export const ABHACreator: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { xToken, authData, authConfig, transactionId, userProfile } = state;
    const [abhaAddress, setAbhaAddress] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [addressMode, setAddressMode] = useState<AddressMode>('custom');

    // Existing ABHA Address (if beneficiary already has one from OTP verify detection)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const existingAbhaAddress = (userProfile as any)?.preferredAbhaAddress
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        || (userProfile as any)?.abhaAddress
        || userProfile?.abhaAddress || '';
    const hasExistingAddress = !!existingAbhaAddress;

    // Fetch suggestions: first try context (pre-fetched), then fetch fresh
    useEffect(() => {
        if (state.abhaSuggestions?.length > 0) {
            setSuggestions(state.abhaSuggestions);
            return;
        }
        const fetchSuggestions = async () => {
            if (!transactionId || !xToken) return;
            try {
                const list = await getAbhaSuggestions(authData!, transactionId, xToken);
                setSuggestions(list);
                dispatch({ type: 'SET_ABHA_SUGGESTIONS', payload: list });
            } catch (err) {
                console.warn('Failed to load suggestions', err);
            }
        };
        fetchSuggestions();
    }, [transactionId, xToken, authData, state.abhaSuggestions, dispatch]);

    // Set initial mode: existing (if available), else custom
    useEffect(() => {
        if (hasExistingAddress) {
            setAbhaAddress(existingAbhaAddress);
            setAddressMode('existing');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasExistingAddress]);

    const onCreate = async () => {
        setLoading(true);
        setError('');
        try {
            if (addressMode === 'existing') {
                // For existing address, skip creation — go straight to success
                dispatch({ type: 'SET_STEP', payload: 'success' });
                return;
            }
            const { profile, xToken: newXToken } = await handleCreateABHA(authData!, transactionId, abhaAddress, xToken, authConfig);
            dispatch({ type: 'SET_USER_PROFILE', payload: profile });
            dispatch({ type: 'SET_X_TOKEN', payload: newXToken });
            dispatch({ type: 'SET_STEP', payload: 'success' });
        } catch (err) {
            const msg = ((err as Error).message || '').toLowerCase();
            if (msg.includes('already') || msg.includes('exist') || msg.includes('taken') || msg.includes('unavailable') || msg.includes('duplicate')) {
                setError('This ABHA Address already exists. Please use a different ABHA Address.');
            } else {
                setError((err as Error).message);
            }
        } finally {
            setLoading(false);
        }
    };

    const validateAbhaAddress = (val: string) => {
        const lengthValid = val.length >= 8 && val.length <= 18;
        const dotCount = (val.match(/\./g) || []).length;
        const underscoreCount = (val.match(/_/g) || []).length;
        const specialCharsValid = dotCount <= 1 && underscoreCount <= 1;
        const startEndValid = val.length >= 2 && /^[a-zA-Z0-9]/.test(val) && /[a-zA-Z0-9]$/.test(val);
        const hasLetters = /[a-zA-Z]/.test(val);
        const hasNumbers = /[0-9]/.test(val);
        const alphanumericMix = hasLetters && hasNumbers;
        const allowedChars = /^[a-zA-Z0-9._]+$/.test(val);

        return {
            isValid: lengthValid && specialCharsValid && startEndValid && alphanumericMix && allowedChars,
            length: lengthValid,
            specialChars: specialCharsValid,
            startEnd: startEndValid,
            alphanumericMix,
            allowedChars,
        };
    };

    const handleSelectMode = (mode: AddressMode, value?: string) => {
        setAddressMode(mode);
        setError('');
        if (mode === 'existing') {
            setAbhaAddress(existingAbhaAddress);
        } else if (mode === 'suggestion' && value) {
            setAbhaAddress(value);
        } else if (mode === 'custom') {
            setAbhaAddress('');
        }
    };

    const validation = validateAbhaAddress(abhaAddress);
    const isSuggestionPick = suggestions.includes(abhaAddress);
    const showValidation = addressMode === 'custom' && abhaAddress.length > 0 && !isSuggestionPick;
    const canSubmit = addressMode === 'existing'
        ? true
        : addressMode === 'suggestion'
            ? abhaAddress.length > 0
            : (validation.isValid || isSuggestionPick);

    if (!authData) return null;

    return (
        <div className="step-container">
            <h2>Choose ABHA Address</h2>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
                Your Aadhaar has been verified. Now choose an ABHA address for your health ID.
            </p>

            {/* ── Option 1: Continue with Existing ABHA Address ────────── */}
            {hasExistingAddress && (
                <div
                    onClick={() => handleSelectMode('existing')}
                    style={{
                        background: addressMode === 'existing' ? 'rgba(34,197,94,0.08)' : '#f8fafc',
                        border: `2px solid ${addressMode === 'existing' ? '#22c55e' : '#e2e8f0'}`,
                        borderRadius: '12px',
                        padding: '12px 16px',
                        marginBottom: '0.75rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}
                >
                    <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: `2px solid ${addressMode === 'existing' ? '#22c55e' : '#cbd5e1'}`,
                        background: addressMode === 'existing' ? '#22c55e' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        {addressMode === 'existing' && <span style={{ color: '#fff', fontSize: '0.7rem' }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                            <span style={{ fontSize: '0.85rem' }}>👤</span>
                            <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>Continue with Existing ABHA Address</span>
                        </div>
                        <span style={{
                            fontFamily: 'monospace', fontSize: '0.9rem',
                            color: '#22c55e', fontWeight: 800, letterSpacing: '0.5px',
                        }}>
                            {existingAbhaAddress}
                        </span>
                    </div>
                </div>
            )}

            {/* ── Option 2: Suggested ABHA Addresses ────────── */}
            {suggestions.length > 0 && !hasExistingAddress && (
                <div
                    onClick={() => {
                        if (addressMode !== 'suggestion') {
                            handleSelectMode('suggestion', suggestions[0]);
                        }
                    }}
                    style={{
                        background: addressMode === 'suggestion' ? 'rgba(99,102,241,0.06)' : '#f8fafc',
                        border: `2px solid ${addressMode === 'suggestion' ? '#6366f1' : '#e2e8f0'}`,
                        borderRadius: '12px',
                        padding: '12px 16px',
                        marginBottom: '0.75rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: addressMode === 'suggestion' ? '10px' : 0 }}>
                        <div style={{
                            width: 20, height: 20, borderRadius: '50%',
                            border: `2px solid ${addressMode === 'suggestion' ? '#6366f1' : '#cbd5e1'}`,
                            background: addressMode === 'suggestion' ? '#6366f1' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            {addressMode === 'suggestion' && <span style={{ color: '#fff', fontSize: '0.7rem' }}>✓</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '0.85rem' }}>📋</span>
                            <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                                Suggested ABHA Addresses ({suggestions.length})
                            </span>
                        </div>
                    </div>

                    {addressMode === 'suggestion' && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginLeft: '32px' }}>
                            {suggestions.map((sug) => (
                                <button
                                    key={sug}
                                    onClick={(e) => { e.stopPropagation(); setAbhaAddress(sug); setError(''); }}
                                    style={{
                                        background: abhaAddress === sug ? '#6366f1' : '#fff',
                                        color: abhaAddress === sug ? '#fff' : '#334155',
                                        border: `1.5px solid ${abhaAddress === sug ? '#6366f1' : '#e2e8f0'}`,
                                        padding: '5px 14px',
                                        borderRadius: '20px',
                                        cursor: 'pointer',
                                        fontSize: '0.82rem',
                                        transition: 'all 0.2s',
                                        fontWeight: 600,
                                    }}
                                >
                                    {sug}@sbx
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Option 3: Custom ABHA Address ────────── */}
            <div
                onClick={() => { if (addressMode !== 'custom') handleSelectMode('custom'); }}
                style={{
                    background: addressMode === 'custom' ? 'rgba(59,130,246,0.06)' : '#f8fafc',
                    border: `2px solid ${addressMode === 'custom' ? '#3b82f6' : '#e2e8f0'}`,
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: addressMode === 'custom' ? '10px' : 0 }}>
                    <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: `2px solid ${addressMode === 'custom' ? '#3b82f6' : '#cbd5e1'}`,
                        background: addressMode === 'custom' ? '#3b82f6' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        {addressMode === 'custom' && <span style={{ color: '#fff', fontSize: '0.7rem' }}>✓</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.85rem' }}>✏️</span>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                            Create Custom ABHA Address
                        </span>
                    </div>
                </div>

                {addressMode === 'custom' && (
                    <div style={{ marginLeft: '32px' }} onClick={(e) => e.stopPropagation()}>
                        {/* Show suggestions inline for existing users */}
                        {suggestions.length > 0 && hasExistingAddress && (
                            <div style={{ marginBottom: '10px' }}>
                                <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Or pick from Suggested Addresses ({suggestions.length})
                                </p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {suggestions.map((sug) => (
                                        <button
                                            key={sug}
                                            onClick={() => { setAbhaAddress(sug); setError(''); }}
                                            style={{
                                                background: abhaAddress === sug ? '#6366f1' : '#fff',
                                                color: abhaAddress === sug ? '#fff' : '#334155',
                                                border: `1.5px solid ${abhaAddress === sug ? '#6366f1' : '#e2e8f0'}`,
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                cursor: 'pointer',
                                                fontSize: '0.78rem',
                                                transition: 'all 0.2s',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {sug}@sbx
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Custom Input */}
                        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    value={abhaAddress}
                                    onChange={(e) => setAbhaAddress(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                                    placeholder="e.g. name.year"
                                    autoFocus
                                    style={{
                                        flex: 1,
                                        paddingRight: '50px',
                                        ...(showValidation ? {
                                            borderColor: validation.isValid ? '#22c55e' : '#ef4444',
                                            boxShadow: `0 0 0 2px ${validation.isValid ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'}`
                                        } : {})
                                    }}
                                />
                                <span style={{
                                    position: 'absolute', right: '14px',
                                    color: '#94a3b8', fontWeight: 700, fontSize: '0.88rem',
                                }}>
                                    @sbx
                                </span>
                            </div>

                            {/* Live Validation Rules */}
                            {showValidation && (
                                <div style={{
                                    marginTop: '10px',
                                    padding: '10px 12px',
                                    background: validation.isValid ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.04)',
                                    border: `1px solid ${validation.isValid ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.15)'}`,
                                    borderRadius: '8px',
                                }}>
                                    <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Validation Summary
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {[
                                            { check: validation.length, label: '1. Minimum length - 8 characters' },
                                            { check: validation.length, label: '2. Maximum length - 18 characters' },
                                            { check: validation.specialChars, label: '3. Special characters allowed - 1 dot (.) and/or 1 underscore (_)' },
                                            { check: validation.startEnd, label: '4. Dot and underscore must be between characters (not at start/end)' },
                                            { check: validation.alphanumericMix, label: '5. Must contain both letters AND numbers' },
                                        ].map((rule, i) => (
                                            <div key={i} style={{
                                                fontSize: '0.74rem',
                                                color: rule.check ? '#16a34a' : '#dc2626',
                                                display: 'flex', alignItems: 'flex-start', gap: '6px',
                                                fontWeight: rule.check ? 500 : 600,
                                                lineHeight: 1.4,
                                            }}>
                                                <span style={{ flexShrink: 0, marginTop: '1px' }}>
                                                    {rule.check ? '✓' : '✕'}
                                                </span>
                                                <span>{rule.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Submit Button */}
            <button
                onClick={onCreate}
                disabled={loading || !canSubmit}
                className="btn-primary"
            >
                {loading
                    ? '⚡ Creating ABHA...'
                    : addressMode === 'existing'
                        ? '✅ Continue with Existing ABHA Address'
                        : addressMode === 'suggestion'
                            ? `💾 Create ABHA as ${abhaAddress}@sbx`
                            : `💾 Create Custom ABHA Address`
                }
            </button>

            {error && <div className="error-message">Error: {error}</div>}

            <button
                onClick={() => dispatch({ type: 'RESET_FLOW' })}
                className="btn-outline"
                style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}
            >
                ✕ Cancel & Start Over
            </button>
        </div>
    );
};
